import { cn } from '@/lib/utils'

/**
 * The words and colours this module renders codes as.
 *
 * The server serves the same vocabulary at `/tally/returns/vocabulary` and that is the
 * authority for WHICH codes exist. What lives here is presentation — a colour and a shape
 * are not something an API should dictate — with a fallback that titles any code this file
 * has not been taught, so a new status renders as words rather than as nothing.
 */

const RETURN_STATUS: Record<string, { label: string; tone: string }> = {
  PENDING: { label: 'Not filed', tone: 'bg-muted text-muted-foreground' },
  SUBMITTED: {
    label: 'Entered',
    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  },
  EXTRACTED: {
    label: 'Read by machine',
    tone: 'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  },
  VERIFIED: {
    label: 'Verified',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  CERTIFIED: {
    label: 'Certified',
    /*
       NO dark: override, deliberately.

       `--chocolate` and `--chocolate-soft` SWAP ROLES between themes — light mode has a deep
       accent on a pale soft, dark mode a light accent on a deep soft. So this one pairing
       works in both, and index.css has already measured it at 6.83:1 on the dark card. The
       first draft added `dark:bg-chocolate/25 dark:text-chocolate-soft`, which put the DARK
       token on a light-ish fill and made the module's most important status unreadable.
    */
    tone: 'bg-chocolate-soft/60 text-chocolate',
  },
  DISPUTED: {
    label: 'Disputed',
    tone: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  },
  REJECTED: {
    label: 'Rejected',
    tone: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  },
}

const FORM_STATUS: Record<string, { label: string; tone: string }> = {
  UPLOADED: { label: 'Uploaded', tone: 'bg-muted text-muted-foreground' },
  EXTRACTING: {
    label: 'Being read',
    tone: 'bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200',
  },
  EXTRACTED: {
    label: 'Read, not committed',
    tone: 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200',
  },
  FAILED: {
    label: 'Could not be read',
    tone: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  },
  COMMITTED: {
    label: 'Committed',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
}

const RESOLUTION: Record<string, string> = {
  PENDING: 'Unsettled',
  ACCEPTED_OCR: 'Machine was right',
  ACCEPTED_ENTRY: 'Entry was right',
  AMENDED: 'Amended',
}

const SEAT: Record<string, string> = {
  PRESIDENT: 'President',
  GOVERNOR: 'Governor',
  SENATOR: 'Senator',
  WOMAN_REP: 'Woman Representative',
  MP: 'Member of Parliament',
  MCA: 'Member of County Assembly',
}

/** Ballot order, which is also the order a tallying hall works through the races. */
export const SEAT_ORDER = ['PRESIDENT', 'GOVERNOR', 'SENATOR', 'WOMAN_REP', 'MP', 'MCA']

const title = (code: string) => {
  const words = code.replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const seatLabel = (code?: string | null) => (code ? SEAT[code] ?? title(code) : '—')
export const resolutionLabel = (code?: string | null) =>
  code ? RESOLUTION[code] ?? title(code) : '—'
/** `FORM_34A` → "Form 34A" — the word people actually say in a tallying hall. */
export const formLabel = (code?: string | null) =>
  !code ? '—' : code.startsWith('FORM_') ? `Form ${code.slice(5)}` : title(code)

export function ReturnStatusChip({ code }: { code: string }) {
  const s = RETURN_STATUS[code] ?? { label: title(code), tone: 'bg-muted text-muted-foreground' }
  return (
    <span className={cn('inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-medium', s.tone)}>
      {s.label}
    </span>
  )
}

export function FormStatusChip({ code }: { code: string }) {
  const s = FORM_STATUS[code] ?? { label: title(code), tone: 'bg-muted text-muted-foreground' }
  return (
    <span className={cn('inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-medium', s.tone)}>
      {s.label}
    </span>
  )
}

/** A party's colour, or the neutral chip an independent gets. */
export function TicketChip({
  shortName, colourHex, independent,
}: { shortName?: string | null; colourHex?: string | null; independent?: boolean }) {
  if (independent) {
    return (
      <span className="inline-block rounded-sm border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
        Independent
      </span>
    )
  }
  if (!shortName) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-1 text-erp-xs">
      <span aria-hidden className="h-2 w-2 rounded-full shrink-0"
        style={{ background: colourHex ?? 'var(--color-muted-foreground)' }} />
      {shortName}
    </span>
  )
}

/**
 * What a figure counted — rendered beside every total, always.
 *
 * A board that does not say what it counted leaves its reader unable to tell a low turnout
 * from a slow count, and one that silently includes unverified returns announces a result
 * nobody stands behind. This is a one-line component because it must never be worth skipping.
 */
export function CountedStatuses({ statuses }: { statuses: string[] }) {
  const words = statuses.map((s) => RETURN_STATUS[s]?.label ?? title(s)).join(' and ')
  return (
    <span className="text-erp-xs text-muted-foreground">
      Counting {words.toLowerCase() || 'nothing'} returns only
    </span>
  )
}
